import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Clock3, ChevronDown, ChevronUp, BadgeCheck } from 'lucide-react';
import api, { p2pAPI, contactsAPI } from '../../services/api';
import { OnlineDot } from '../agent/AgentOffers';

const METHOD_LABELS = {
  zelle: 'Zelle',
  usdt_trc20: 'USDT (TRC-20)',
  stripe: 'Tarjeta',
};

const RATE_KEY = { USD: 'usd_cup', EUR: 'eur_cup', USDT: 'usdt_cup' };

function StarDisplay({ rating }) {
  if (!rating) return <span style={{ fontSize: 12, color: 'var(--faint)' }}>Sin calificaciones</span>;
  const r = Math.round(parseFloat(rating));
  return (
    <span>
      <span style={{ color: '#f59e0b', fontSize: 14 }}>{'★'.repeat(r)}{'☆'.repeat(5 - r)}</span>
      <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 4 }}>{parseFloat(rating).toFixed(1)}</span>
    </span>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('es', { maximumFractionDigits: 2 });
}

// ── Panel de toma (quote + take) ──────────────────────────────────────
function TakePanel({ offer, contacts, onDone }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState((offer.payment_methods || [])[0] || '');
  const [mode, setMode] = useState('contact');
  const [contactId, setContactId] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  const [quote, setQuote] = useState(null);
  const [secsLeft, setSecsLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!quote) return;
    const end = new Date(quote.rate_expires_at).getTime();
    const tick = () => setSecsLeft(Math.max(0, Math.round((end - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [quote]);

  const doQuote = async () => {
    setError(''); setQuote(null);
    const amt = parseFloat(amount);
    if (!amt || amt < offer.min_amount || amt > offer.max_amount) {
      setError(`Monto entre $${offer.min_amount} y $${offer.max_amount}`);
      return;
    }
    setLoading(true);
    const res = await p2pAPI.quoteOffer(offer.id, amt).catch((e) => {
      setError(e.response?.data?.detail || 'No se pudo cotizar'); return null;
    });
    setLoading(false);
    if (res) setQuote(res.data);
  };

  const doTake = async () => {
    setError('');
    let recipient_name, recipient_phone, contact_id = null;
    if (mode === 'contact') {
      const c = contacts.find((x) => x.id === contactId);
      if (!c) { setError('Elegí un contacto'); return; }
      recipient_name = c.full_name; recipient_phone = c.phone_cuba; contact_id = c.id;
    } else {
      if (!manualPhone) { setError('Ingresá el teléfono del beneficiario'); return; }
      recipient_name = manualName || null; recipient_phone = manualPhone;
    }
    setLoading(true);
    const res = await p2pAPI.takeOffer(offer.id, {
      amount_usd: parseFloat(amount),
      recipient_phone, recipient_name, contact_id,
      payment_method: method,
    }).catch((e) => {
      setError(e.response?.data?.detail || 'No se pudo tomar el anuncio'); return null;
    });
    setLoading(false);
    if (res?.data?.ref) onDone(res.data.ref);
  };

  const locked = quote && secsLeft > 0;

  return (
    <div style={{ borderTop: '1px solid #f1f1f4', padding: '16px 20px', background: 'var(--bg)' }}>
      <label style={lbl}>Monto a enviar (USD)</label>
      <input style={inp} type="number" value={amount} placeholder={`${offer.min_amount}–${offer.max_amount}`}
        onChange={(e) => { setAmount(e.target.value); setQuote(null); }} />

      <label style={lbl}>Pagás con</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {(offer.payment_methods || []).map((m) => (
          <button key={m} onClick={() => setMethod(m)} style={chip(method === m)}>
            {METHOD_LABELS[m] || m}
          </button>
        ))}
      </div>

      <label style={lbl}>Beneficiario</label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button onClick={() => setMode('contact')} style={chip(mode === 'contact')}>Mis contactos</button>
        <button onClick={() => setMode('manual')} style={chip(mode === 'manual')}>Escribir nuevo</button>
      </div>
      {mode === 'contact' ? (
        <select style={inp} value={contactId} onChange={(e) => setContactId(e.target.value)}>
          <option value="">Elegí un contacto…</option>
          {contacts.map((c) => (
            <option key={c.id} value={c.id}>{c.full_name} — {c.phone_cuba}</option>
          ))}
        </select>
      ) : (
        <>
          <input style={inp} placeholder="Nombre (opcional)" value={manualName}
            onChange={(e) => setManualName(e.target.value)} />
          <input style={inp} placeholder="+53 5234 5678" value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)} />
        </>
      )}

      {quote && (
        <div className="g-card" style={{ padding: 14, margin: '12px 0', borderRadius: 12 }}>
          <Row k="Tasa" v={`${fmt(quote.effective_rate)} CUP`} />
          <Row k="Recibe el beneficiario" v={`${fmt(quote.cup_to_deliver)} CUP`} big />
          <Row k="Comisión" v={`$${fmt(quote.fees?.sender_fee)} (${quote.fees?.sender_fee_pct})`} />
          <Row k="Total a pagar" v={`$${fmt(quote.fees?.sender_pays)}`} big />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: secsLeft > 60 ? '#15803d' : 'var(--orange)', marginTop: 8 }}>
            <Clock3 size={14} />
            {secsLeft > 0
              ? `Tasa fija por ${Math.floor(secsLeft / 60)}:${String(secsLeft % 60).padStart(2, '0')}`
              : 'Tasa vencida — volvé a cotizar'}
          </div>
        </div>
      )}

      {error && <div style={{ color: '#9b2c5e', fontSize: 13, margin: '8px 0' }}>{error}</div>}

      {!locked ? (
        <button onClick={doQuote} disabled={loading} className="g-primary" style={{ width: '100%' }}>
          {loading ? 'Cotizando…' : 'Cotizar'}
        </button>
      ) : (
        <button onClick={doTake} disabled={loading} style={btnConfirm}>
          {loading ? 'Procesando…' : 'Confirmar y tomar'}
        </button>
      )}
    </div>
  );
}

function Row({ k, v, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0' }}>
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{k}</span>
      <span style={{ fontSize: big ? 17 : 13, fontWeight: big ? 800 : 500 }}>{v}</span>
    </div>
  );
}

// ── Card de anuncio ───────────────────────────────────────────────────
function OfferCard({ offer, contacts, onDone, marketRate, best }) {
  const [open, setOpen] = useState(false);

  let vsMarket = null;
  if (offer.pricing_mode === 'ANCHORED') {
    vsMarket = offer.spread_pct;
  } else if (marketRate && offer.effective_rate) {
    vsMarket = ((offer.effective_rate / marketRate) - 1) * 100;
  }

  return (
    <div className="g-card" style={{ overflow: 'hidden', marginBottom: 10 }}>
      <div style={{ padding: '18px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
        onClick={() => setOpen(!open)}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{offer.agent?.name}</span>
            {best && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 20, background: '#e7f8ee', color: '#15803d', fontSize: 11, fontWeight: 800 }}>
                <BadgeCheck size={12} /> Mejor tasa
              </span>
            )}
          </div>
          <div style={{ marginTop: 3 }}><StarDisplay rating={offer.agent?.rating} /></div>
          <div style={{ marginTop: 5 }}><OnlineDot online={offer.agent?.is_online} size={8} /></div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
            {(offer.payment_methods || []).map((m) => (
              <span key={m} style={{ padding: '2px 8px', borderRadius: 20, background: 'var(--bg)', border: '1px solid var(--card-border)', fontSize: 11, fontWeight: 600 }}>
                {METHOD_LABELS[m] || m}
              </span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: 'Outfit', fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{fmt(offer.effective_rate)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>CUP / {offer.pay_currency}</div>
          {vsMarket !== null && (
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--faint)', marginTop: 3 }}>
              elToque {vsMarket > 0 ? '+' : ''}{fmt(vsMarket)}%
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            ${offer.min_amount}–{offer.max_amount} {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </div>
        </div>
      </div>
      {open && <TakePanel offer={offer} contacts={contacts} onDone={onDone} />}
    </div>
  );
}

// ── Pantalla ──────────────────────────────────────────────────────────
export default function P2PMarket() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [o, c, r] = await Promise.all([
      p2pAPI.listOffers().catch(() => ({ data: [] })),
      contactsAPI.list().catch(() => ({ data: [] })),
      api.get('/api/v1/rates/').then(x => x.data).catch(() => null),
    ]);
    setOffers(Array.isArray(o.data) ? o.data : []);
    setContacts(Array.isArray(c.data) ? c.data : []);
    setRates(r);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDone = () => navigate('/app/historial');

  const sorted = [...offers].sort((a, b) => (b.effective_rate || 0) - (a.effective_rate || 0));

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <h1 style={{ fontSize: 27, margin: '0 0 4px', letterSpacing: '-1px' }}>Mercado P2P</h1>
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>
        Elegí un agente por su tasa. La tasa se ancla al mercado informal (elToque).
      </p>

      {rates?.usd_cup && (
        <div className="g-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 14 }}>
          <TrendingUp size={18} color="var(--orange)" />
          <div style={{ fontSize: 13 }}>
            <b>Mercado elToque:</b> {fmt(rates.usd_cup)} CUP/USD
            {rates.usdt_cup ? <> · {fmt(rates.usdt_cup)} CUP/USDT</> : null}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Cargando anuncios…</div>
      ) : sorted.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No hay anuncios activos por ahora.</div>
      ) : (
        sorted.map((o, i) => (
          <OfferCard key={o.id} offer={o} contacts={contacts} onDone={onDone}
            marketRate={rates ? rates[RATE_KEY[o.pay_currency]] : null}
            best={i === 0 && sorted.length > 1} />
        ))
      )}
    </div>
  );
}

// ── estilos ───────────────────────────────────────────────────────────
const inp = { width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid var(--card-border)', fontSize: 14, marginBottom: 10, boxSizing: 'border-box', background: '#fff' };
const lbl = { display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--faint)', marginBottom: 6 };
const chip = (on) => ({ padding: '7px 12px', borderRadius: 20, border: `1px solid ${on ? 'var(--orange)' : 'var(--card-border)'}`, background: on ? 'var(--orange-grad)' : '#fff', color: on ? '#fff' : '#4a4035', fontSize: 12, fontWeight: 600, cursor: 'pointer' });
const btnConfirm = { width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: '#15803d', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' };
